import React, { useEffect, useCallback, useState } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Wizard as AlbusWizard, Steps, Step } from 'react-albus';
import { uniq, isEqual } from 'lodash';
import VerticalStepper from '../VerticalWizardWrapper/VerticalStepper';
import { clearAllNotifications } from '../../helpers/notificationHelpers';

const Wizard = (props) => {
  const {
    match: { url },
    history,
    stepContainers,
    stepParams,
    currentStep,
    setCurrentStep,
    unlockedSteps,
    setUnlockedSteps,
    scrollToTop,
    goToStep,
    isDeviceMdSize,
  } = props;

  const [activeStep, setActiveStep] = useState(currentStep.n - 1);

  useEffect(() => {
    setActiveStep(currentStep.n - 1);
  }, [currentStep.n]);

  const push = useCallback(
    (step) => {
      const path = `${url}/${step.id}`;
      history.push(path);
    },
    [history, url],
  );

  const goTo = useCallback(
    (step) => {
      push(step);
      setCurrentStep(step);
    },
    [setCurrentStep, push],
  );

  const blockNextSteps = useCallback(() => {
    const stepsUpToCurrentStep = stepContainers
      .filter(({ n }) => n <= currentStep.n)
      .map(({ id }) => id);

    setUnlockedSteps((currentUnlockedSteps) => {
      if (!isEqual(currentUnlockedSteps, stepsUpToCurrentStep)) {
        return stepsUpToCurrentStep;
      }
      return currentUnlockedSteps;
    });
  }, [currentStep.n, setUnlockedSteps, stepContainers]);

  const getStep = (stepIndex) => stepContainers[stepIndex] || null;

  useEffect(() => {
    // every time wizard step changes this code works
    if (scrollToTop) {
      window.scrollTo(0, 0);
    }
  }, [currentStep, scrollToTop]);

  useEffect(() => {
    const { pathname } = history.location;
    const id = pathname.replace(`${url}/`, '');

    const step = stepContainers.find((s) => s.id === id);

    if (!step || !unlockedSteps.includes(step.id)) {
      history.push(url);
    } else {
      setCurrentStep(step);
    }
  }, [history, history.location.pathname, setCurrentStep, stepContainers, url, unlockedSteps]);

  const handleGoToStep = (step, index) => {
    setActiveStep(index);
    goToStep(step);
  };

  return (
    <AlbusWizard
      history={history}
      basename={url}
      render={() => (
        <Steps step={currentStep && { id: currentStep.id }}>
          {stepContainers
            .filter((s) => s.content)
            .map((s) => {
              const { id } = s;
              const StepContent = s.content;

              const wizardBag = {
                goNext: (params) => {
                  setActiveStep((prevActiveStep) => prevActiveStep + 1);
                  const index = stepContainers.indexOf(s);
                  const nextStep = getStep(index + 1);
                  clearAllNotifications();
                  if (nextStep) {
                    setUnlockedSteps((current) => uniq([...current, nextStep.id]));
                    goTo(nextStep, params);
                  }
                },
                goPrevious: (params) => {
                  setActiveStep((prevActiveStep) => prevActiveStep - 1);
                  const index = stepContainers.indexOf(s);
                  const previousStep = getStep(index - 1);
                  clearAllNotifications();
                  if (previousStep) {
                    goTo(previousStep, params);
                  }
                },
                config: stepContainers,
                step: s,
              };

              const content = (
                <StepContent
                  goNext={wizardBag.goNext}
                  goPrevious={wizardBag.goPrevious}
                  blockNextSteps={blockNextSteps}
                  config={wizardBag.config}
                  step={wizardBag.step}
                  steps={stepContainers}
                  setUnlockedSteps={setUnlockedSteps}
                  params={stepParams}
                  unlockedSteps={unlockedSteps}
                />
              );

              return (
                <Step id={id} key={id}>
                  {isDeviceMdSize ? (
                    <VerticalStepper
                      content={content}
                      currentStep={currentStep}
                      steps={stepContainers}
                      activeStep={activeStep}
                      unlockedSteps={unlockedSteps}
                      handleGoToStep={handleGoToStep}
                    />
                  ) : (
                    content
                  )}
                </Step>
              );
            })}
        </Steps>
      )}
    />
  );
};

Wizard.propTypes = {
  currentStep: PropTypes.shape({ id: PropTypes.string, n: PropTypes.number }),
  setCurrentStep: PropTypes.func,
  stepContainers: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.func,
      id: PropTypes.string,
      isBlocked: PropTypes.func,
    }),
  ),
  stepParams: PropTypes.shape({}),
  unlockedSteps: PropTypes.arrayOf(PropTypes.string),
  setUnlockedSteps: PropTypes.func,
  match: PropTypes.shape({
    url: PropTypes.string,
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }),
  scrollToTop: PropTypes.bool,
  goToStep: PropTypes.func,
  isDeviceMdSize: PropTypes.bool,
};

Wizard.defaultProps = {
  stepContainers: [],
  stepParams: {},
  unlockedSteps: [],
  match: {
    url: '',
  },
  history: {},
  currentStep: { id: '' },
  setCurrentStep: () => {},
  setUnlockedSteps: () => {},
  scrollToTop: false,
  goToStep: () => {},
  isDeviceMdSize: false,
};

export default withRouter(Wizard);
